import {useMemo, useState} from 'react';
import isEqual from 'lodash/isEqual';

export function useUndoRedo<T>(initialState: T, maxIndex = 20) {
  const [states, setStates] = useState([initialState]);
  const [index, setIndex] = useState(0);

  const state = useMemo(() => states[index], [states, index]);
  const setState = (value: T) => {
    if (isEqual(state, value)) {
      return;
    }

    if (states.length === maxIndex) {
      const copy = states.slice(1);
      copy.push(value);
      setStates(copy);
      return;
    }

    // remove all states after current index
    const copy = states.slice(0, index + 1);
    copy.push(value);
    setStates(copy);
    setIndex(copy.length - 1);
  };

  const goBack = () => {
    const newIndex = Math.max(0, index - 1);
    setIndex(newIndex);
    return states[newIndex];
  };

  const goForward = () => {
    const newIndex = Math.min(states.length - 1, index + 1);
    setIndex(newIndex);
    return states[newIndex];
  };

  const setInitialState = (initialState: T) => {
    setStates([initialState]);
    setIndex(0);
  };

  const reset = () => {
    setStates([initialState]);
    setIndex(0);
  };

  return {
    state,
    setState,
    goBack,
    goForward,
    reset,
    setInitialState,
  };
}
